<template>
  <div v-if="showVerifyModal" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content verify-modal">
      <!-- Modal Header -->
      <div class="modal-header-premium">
        <div class="header-icon">
          <i class="fas" :class="isRecoveryMode ? 'fa-key' : 'fa-shield-alt'"></i>
        </div>
        <div class="header-text">
          <h4>{{ isRecoveryMode ? 'Account Recovery' : 'Two-Factor Challenge' }}</h4>
          <p>{{ isRecoveryMode ? 'Access account with recovery code' : 'Verify your identity to continue' }}</p>
        </div>
        <button class="close-btn" @click="closeModal">&times;</button>
      </div>

      <div class="modal-body-premium">
        <!-- Authenticator Code Mode -->
        <div v-if="!isRecoveryMode" class="verify-step">
          <p class="description-text">
            Enter the 6-digit verification code generated by your authenticator app.
          </p>

          <div class="code-input-section">
            <o-input-digit 
              v-model="code" 
              placeholder="000000" 
              :maxlength="6" 
              class="modern-digit-input"
              @keyup.enter="handleVerify"
            />
          </div>
        </div>

        <!-- Recovery Code Mode -->
        <div v-else class="verify-step">
          <p class="description-text">
            Enter one of your 8-character recovery codes to bypass authenticator verification.
          </p>

          <div class="form-group-clean">
            <label>Recovery Code</label>
            <input 
              v-model="recoveryCode" 
              type="text" 
              class="input-clean uppercase" 
              placeholder="e.g. 12345678"
              @keyup.enter="handleVerify"
            />
          </div>
        </div>

        <!-- Error Feedback -->
        <div v-if="error" class="error-feedback-clean">
          <i class="fas fa-exclamation-circle"></i>
          <span>{{ error }}</span>
        </div>

        <!-- Mode Toggle -->
        <div class="mode-switch">
          <button class="btn-link-clean" @click="toggleMode">
            {{ isRecoveryMode ? 'Use Authenticator App instead' : 'Lost your device? Use Recovery Code' }}
          </button>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer-premium">
        <button class="btn-secondary-clean" @click="closeModal">Cancel</button>
        <button 
          class="btn-primary-premium" 
          @click="handleVerify" 
          :disabled="loading || (!isRecoveryMode && code.length < 6) || (isRecoveryMode && !recoveryCode)"
        >
          <span v-if="loading"><i class="fas fa-spinner fa-spin me-2"></i> Verifying...</span>
          <span v-else>{{ isRecoveryMode ? 'Recover Access' : 'Verify & Continue' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { OInputDigit } from '@/components/OmnihControl'
import { setSwipeBackEnabled } from '@/services/swipeBack'

/**
 * 2FA verification component used during the login flow.
 * Supports both TOTP codes and backup recovery codes with a premium UI.
 */
export default {
  name: 'TwoFactorVerify',
  components: { OInputDigit },
  mounted() {
    setSwipeBackEnabled(false);
  },
  unmounted() {
    setSwipeBackEnabled(true);
  },
  data() {
    return {
      code: '',
      recoveryCode: '',
      loading: false,
      error: '',
      isRecoveryMode: false
    }
  },
  computed: {
    ...mapGetters('TwoFactor', ['showVerifyModal']),
  },
  methods: {
    ...mapMutations('TwoFactor', ['SET_SHOW_VERIFY_MODAL']),
    ...mapActions('TwoFactor', ['verifyLogin', 'recoverLogin']),
    
    /**
     * Switch between TOTP and Recovery Code modes.
     */
    toggleMode() {
        this.isRecoveryMode = !this.isRecoveryMode
        this.error = ''
        this.code = ''
        this.recoveryCode = ''
    },
    
    /**
     * Close verification modal and reset local state.
     */
    closeModal() {
        this.SET_SHOW_VERIFY_MODAL(false)
        this.error = ''
        this.code = ''
        this.recoveryCode = ''
        this.isRecoveryMode = false
    },
    
    /**
     * Submit the verification code to the appropriate API endpoint.
     */
    async handleVerify() {
      this.loading = true
      this.error = ''
      
      try {
        let result
        if (this.isRecoveryMode) {
            if (!this.recoveryCode) {
                this.error = 'Recovery code is required'
                this.loading = false
                return
            }
            result = await this.recoverLogin(this.recoveryCode)
        } else {
            if (!this.code || this.code.length < 6) {
                this.error = 'Verification code must be 6 digits'
                this.loading = false
                return
            }
            result = await this.verifyLogin(this.code)
        }
        
        if (result && result.success) {
            const user = result.user
            
            // Success: Close modal and navigate to dashboard
            this.closeModal()
            if (user.level === 'member') {
                this.$router.replace({ name: 'client_dashboard' })
            } else {
                this.$router.replace({ name: 'Home' })
            }
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'The provided code is invalid.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2100; /* Higher than Login screens */
}

.verify-modal {
    background: white;
    width: 90%;
    max-width: 460px;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
}

.modal-header-premium {
    padding: 32px 32px 24px;
    background: #fcfcfc;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    gap: 20px;
    position: relative;
}

.header-icon {
    width: 52px;
    height: 52px;
    background: var(--secondary-color, rgba(252, 102, 102, 0.1));
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #fc6666);
    font-size: 24px;
}

.header-text h4 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #111827;
}

.header-text p {
    margin: 4px 0 0;
    font-size: 14px;
    color: #6b7280;
}

.modal-body-premium {
    padding: 32px;
}

.description-text {
    font-size: 15px;
    color: #4b5563;
    line-height: 1.6;
    margin-bottom: 32px;
    text-align: center;
}

.code-input-section {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
}

.form-group-clean label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 10px;
}

.input-clean {
    width: 100%;
    padding: 14px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    font-size: 16px;
    text-align: center;
    letter-spacing: 2px;
    outline: none;
    transition: all 0.2s;
}

.uppercase {
    text-transform: uppercase;
}

.input-clean:focus {
    border-color: var(--primary-color, #fc6666);
    box-shadow: 0 0 0 4px var(--secondary-color, rgba(252, 102, 102, 0.1));
}

.error-feedback-clean {
    margin-top: 24px;
    padding: 14px;
    background: #fef2f2;
    color: #dc2626;
    border-radius: 12px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.mode-switch {
    margin-top: 32px;
    text-align: center;
}

.btn-link-clean {
    background: none;
    border: none;
    color: #6366f1;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
}

.btn-link-clean:hover {
    color: #4f46e5;
    text-decoration: underline;
}

.modal-footer-premium {
    padding: 24px 32px;
    background: #f9fafb;
    border-top: 1px solid #f3f4f6;
    display: flex;
    justify-content: flex-end;
    gap: 16px;
}

.btn-secondary-clean {
    padding: 12px 24px;
    border: 1px solid #e5e7eb;
    background: white;
    color: #4b5563;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.btn-primary-premium {
    padding: 12px 32px;
    background: var(--primary-color, #fc6666);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 6px -1px var(--secondary-color, rgba(252, 102, 102, 0.25));
}

.btn-primary-premium:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.close-btn {
    position: absolute;
    top: 24px;
    right: 24px;
    background: none;
    border: none;
    font-size: 28px;
    color: #9ca3af;
    cursor: pointer;
}
</style>

